/* eslint-disable react-hooks/rules-of-hooks */
import React, { useEffect, useState } from 'react';
import {
  shape, string, bool, func,
} from 'prop-types';
import { Loading as LoadingPage, FrameContext } from '@hyphengroup/cag-components';
import { withTracking, LOCATIONS, loginLightboxLoadTrack } from 'components/tracking';
import withApplicationConfig from 'containers/withApplicationConfig';
import {
  getStaticText,
  getImages,
  showStaticTextIfSpecified,
} from 'helpers/Utils';
import { withLoginPage } from 'containers/withQueries';
import Signup from 'components/auth/Signup';
import { locations as AUTH_LOCATIONS } from 'containers/withAuth';
import usePageReloadOnHistoryNavigation from 'hooks/usePageReloadOnHistoryNavigation';
import { LoginFromIframeEvents, LoginStates, LoginToIframeEvents } from 'constants/login';
import { withRouter } from 'react-router';
import { createGlobalStyle } from 'styled-components';
import qs from 'query-string';

function sentLoginEvents(events) {
  window.top.postMessage(JSON.stringify(events), process.env.NODE_ENV === 'development' ? '*' : window.origin);
}

function sentConfirmSkipEvents() {
  window.top.postMessage(JSON.stringify({ [LoginFromIframeEvents.ConfirmSkip]: true }), process.env.NODE_ENV === 'development' ? '*' : window.origin);
}

const GlobalStyle = createGlobalStyle`
  #root {
    height: 100vh;
  }
`;

function IframeLogin({
  loginQuery,
  track,
  location,
}) {
  const queryParams = qs.parse(location.search);
  const showSkip = 'showskip' in queryParams;
  const loanRecommend = 'loanrecommend' in queryParams;

  const [authLocation, setAuthLocation] = useState(
      'login' in qs.parse(location.search)
      ? AUTH_LOCATIONS.LOGIN
      : AUTH_LOCATIONS.SIGNUP
  );
  const [pageLoading] = usePageReloadOnHistoryNavigation();
  const { loading, customerProfile } = loginQuery;
  const [isResendOTP, setIsResendOTP] = useState(false);

  if (loading || pageLoading) {
    return <LoadingPage />;
  }

  const images = getImages(loginQuery);
  let staticText = getStaticText(loginQuery);

  if ('loanrecommend' in queryParams) {
    staticText = {
      ...staticText,
      LOGIN_HEADER_SUBTEXT: showStaticTextIfSpecified('SKIP_ENABLED_LOGIN_HEADER_SUBTEXT', staticText) ?? staticText.LOGIN_HEADER_SUBTEXT,
      SIGNUP_HEADER: showStaticTextIfSpecified('SKIP_ENABLED_SIGNUP_HEADER', staticText) ?? staticText.SIGNUP_HEADER,
    };
  }

  useEffect(() => {
    track({ data: { location: LOCATIONS.LOGIN } });
    loginLightboxLoadTrack(track, LOCATIONS.LOGIN, loanRecommend);
    sentLoginEvents({
      [LoginFromIframeEvents.CustomerProfile]: customerProfile,
    });
  }, []);

  useEffect(() => {
    const receiveAuthEvent = (e) => {
      try {
        const events = JSON.parse(e.data);

        Object.keys(events)?.map(eventKey => {
          switch (eventKey) {
            case LoginToIframeEvents.AuthLocation: {
              const newAuthLocation = AUTH_LOCATIONS[events[eventKey]];
              if (newAuthLocation) { setAuthLocation(newAuthLocation); }
              break;
            }

            case LoginToIframeEvents.ResendOTP: {
              setIsResendOTP(true);
              break;
            }

            default: {
              break;
            }
          }
        });
      } catch (_) {
        // message is not a valid JSON object
      }
    };

    window.addEventListener('message', receiveAuthEvent);

    return () => window.removeEventListener('message', receiveAuthEvent);
  }, []);

  if (customerProfile.loginState === LoginStates.LoggedOut) {
    return (
      <FrameContext.Provider value={true}>
        <GlobalStyle />
        <Signup
          isOpen={true}
          isResendOTP={isResendOTP}
          onResendOTPFromIframe={() => setIsResendOTP(false)}
          onClose={() => sentLoginEvents({ [LoginFromIframeEvents.CloseLoginModal]: true })}
          onSuccess={() => sentLoginEvents({
            [LoginFromIframeEvents.LoginState]: LoginStates.LoggedIn,
          })}
          staticText={staticText}
          images={images}
          overlay={false}
          fullscreen={true}
          registerCtaText={staticText.REWARDS_LOGIN_PAGE_SIGNUP_HEADER}
          registerUpperDescription={staticText.REWARDS_LOGIN_PAGE_SIGNUP_DESCRIPTION}
          startLocation={authLocation}
          onOpenSocialLoginFromIframe={url => {
            sentLoginEvents({
              [LoginFromIframeEvents.OpenSocialWindow]: url,
            });
          }}
          onCloseSocialLoginFromIframe={() => {
            sentLoginEvents({ [LoginFromIframeEvents.CloseSocialWindow]: true },);
          }}
          onShowBannerFromIframe={(bannerProps) => {
            sentLoginEvents({ [LoginFromIframeEvents.UpdateBanner]: bannerProps });
          }}
          showSkip={showSkip}
          onConfirmSkip={sentConfirmSkipEvents}
          loanRecommend={loanRecommend}
        />
      </FrameContext.Provider>
    );
  }
  return <LoadingPage />;
}

IframeLogin.propTypes = {
  track: func.isRequired,
  loginQuery: shape({
    loading: bool.isRequired,
    customerProfile: shape({
      loginState: string,
    }),
  }).isRequired,
  location: shape({ location: shape({ search: string }) }),
};

export default withTracking(
  withApplicationConfig(withLoginPage(withRouter(IframeLogin)), ['languages', 'contentstackKeys'])
);
